// GlobalCustomerForm.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button } from "semantic-ui-react";
import SearchDropdown from "../input/SearchDropdown";
import {getFieldValuesByTableAndId, getOtherRefSearchDropdown} from "../../api/keyvalue";
import { useSelector } from "react-redux";
import { createGlobalCustomerFilter, getGlobalCustomerFilterList } from "../../api/GlobalCustomerFilterApi";

const FORM = "GLOBAL_CUSTOMER_MODAL";

export default function GlobalCustomerForm({ mountNode, onClose }) {
    const user = useSelector((state) => state.user);
    const userId = user?.id ?? user?.userId ?? user?.username ?? null;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [customerGroup, setCustomerGroup] = useState(null);
    const [customerSelections, setCustomerSelections] = useState({});
    const [existingUcfIdByCustomer, setExistingUcfIdByCustomer] = useState({}); // 👈 keep ucfId per customer
    const [prefilling, setPrefilling] = useState(false); // 👈 avoid clearing during bootstrap

    const [resetGroup, setResetGroup] = useState(false);
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [resetCustomers, setResetCustomers] = useState(false);

    const searchRef = useRef();


    const customerGroupDep = useMemo(() => ({ type: "customer_group" }), []);
    const groupId = customerGroup?.key ?? customerGroup ?? null;

    const customerDep = useMemo(
        () => ({ type: "customer_group_customer", value: groupId?.toString() ?? "" }),
        [groupId]
    );

    // ✅ clear only when the groupId changes (user action), never on prefilling toggle
    useEffect(() => {
        if (prefilling) {
            console.log('[clear-on-group-change] skipped (prefilling)');
            return;
        }
        if (groupId == null) return; // nothing to clear on initial empty

        console.log('[clear-on-group-change] clearing because groupId changed', { groupId });
        setCustomerSelections({});
        setExistingUcfIdByCustomer({});
        setResetCustomers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]); // <-- remove prefilling from deps


    const handleGroupSelect = (valueOrObj) => {
        const id = valueOrObj?.key ?? valueOrObj ?? null;
        setCustomerGroup(id);
    };
    const handleCancel = () => {
        // clear both dropdowns + any temp state
        setCustomerGroup(null);
        setCustomerSelections({});
        setExistingUcfIdByCustomer({});
        setSearchFieldReset(true);   // resets the group SearchDropdown
        setResetCustomers(true);     // resets the customers SearchDropdown
        setError(null);
        setLoading(false);
        setSaving(false);

        onClose?.();
    };

    // add near other handlers
    const handleClearAll = () => {
        // reset everything in UI
        setCustomerGroup(null);
        setCustomerSelections({});
        setExistingUcfIdByCustomer({});
        setError(null);

        // trigger dropdowns to visually clear
        setSearchFieldReset(true);  // resets the group dropdown
        setResetCustomers(true);    // resets the customers dropdown
    };


    const handleSelectCustomer = (res) => {
        const idRaw = res?.key ?? res?.id ?? res?.value;
        const name = res?.title ?? res?.text ?? (idRaw != null ? String(idRaw) : "Unknown");
        const id = parseInt(idRaw, 10);
        if (Number.isNaN(id)) return;
        setCustomerSelections((prev) => (prev[id] ? prev : { ...prev, [id]: name }));
        // if user picks a customer that already exists, keep its ucfId mapping as-is
    };

    const removeSelection = (id) => {
        setCustomerSelections((prev) => {
            const { [id]: _omit, ...rest } = prev;
            return rest;
        });
        // do not delete existingUcfIdByCustomer entry; omission from payload = delete on server
    };

    // helper: resolve customer id -> label inside a group
    const getCustomerLabelMap = useCallback(async (ids) => {
        if (!Array.isArray(ids) || ids.length === 0) {
            console.log('[getCustomerLabelMap] skip (no ids)', { ids });
            return {};
        }

        console.log('[getCustomerLabelMap] start', { ids });

        // Do individual lookups (safe with current API; switch to CSV batch if your API supports it)
        const pairs = await Promise.all(
            ids.map(async (id) => {
                try {
                    const resp = await getFieldValuesByTableAndId({
                        tableName: "masterdata.tms_m_stakeholder", // ✅ your customer table
                        primaryKey: "stk_id",
                        fieldNames: "stk_name",                     // ✅ display field
                        ids: id.toString(),                         // single id lookup (like your charges sample)
                    });

                    const label = resp?.[0]?.field1 ?? String(id);
                    console.log('[getCustomerLabelMap] ok', { id, label, raw: resp });
                    return [id, label];
                } catch (e) {
                    console.warn('[getCustomerLabelMap] fail -> fallback to id', { id, error: e });
                    return [id, String(id)];
                }
            })
        );

        const out = Object.fromEntries(pairs);
        console.log('[getCustomerLabelMap] done', out);
        return out;
    }, []);

    // --- FETCH on open: prefill group + customers + map customerId->ucfId
    useEffect(() => {
        if (!userId) return;

        let cancelled = false;
        (async () => {
            console.log('[GlobalCustomerForm] prefill start', { userId });
            setLoading(true);
            setError(null);

            try {
                const res = await getGlobalCustomerFilterList(userId);
                console.log('[GlobalCustomerForm] GET response', res);
                const list = res?.data ?? [];

                if (!Array.isArray(list) || list.length === 0) {
                    console.log('[GlobalCustomerForm] no existing selections');
                    return;
                }

                const fetchedGroupId = list[0]?.ucfCustomerGroup ?? null;
                console.log('[GlobalCustomerForm] fetchedGroupId', fetchedGroupId, 'rows:', list.length);

                if (cancelled) return;
                setPrefilling(true);

                setCustomerGroup(fetchedGroupId);
                console.log('[GlobalCustomerForm] setCustomerGroup ->', fetchedGroupId);

                const ids = list
                    .map((r) => r?.ucfCustomer)
                    .filter((v) => v != null)
                    .map((v) => Number(v));
                console.log('[GlobalCustomerForm] customer ids', ids);

                const idMap = {};
                list.forEach((r) => {
                    const cid = Number(r?.ucfCustomer);
                    if (!Number.isNaN(cid) && r?.ucfId != null) idMap[cid] = r.ucfId;
                });
                console.log('[GlobalCustomerForm] existingUcfIdByCustomer', idMap);

                const labelMap = await getCustomerLabelMap(ids); // <- your table-based helper
                if (cancelled) return;

                console.log('[GlobalCustomerForm] hydrated customer labels', labelMap);

                setCustomerSelections(labelMap);    // { id: "Customer Name" }
                setExistingUcfIdByCustomer(idMap);

                // ❌ DO NOT call setResetCustomers(true) here
            } catch (e) {
                console.error('[GlobalCustomerForm] prefill error', e);
                if (!cancelled) setError("Failed to load existing filter.");
            } finally {
                if (!cancelled) {
                    setPrefilling(false);
                    setLoading(false);
                    console.log('[GlobalCustomerForm] prefill done');
                }
            }
        })();

        return () => { cancelled = true; };
    }, [userId, getCustomerLabelMap]);

    useEffect(() => {
        console.log('[GlobalCustomerForm] now:', customerSelections);
    }, [customerSelections]);


    // --- SAVE: create/update one record per selected customer
    const handleSave = useCallback(async () => {
        // build payload if we have a group & customers; otherwise send empty array to mean "clear"
        const customerIds = Object.keys(customerSelections)
            .map((k) => parseInt(k, 10))
            .filter((n) => !Number.isNaN(n));

        let payload = [];

        if (groupId && customerIds.length > 0) {
            const base = {
                ucfId: null,
                ucfCompany: 1,
                ucfReference: null,
                ucfCustomerGroup: Number(groupId),
                ucfUser: String(userId),
                ucfStatus: 2,
                ucfActive: 1,
                createdBy: user?.username ?? String(userId),
            };

            payload = customerIds.map((cid) => ({
                ...base,
                ucfCustomer: cid,
                ucfId: existingUcfIdByCustomer[cid] ?? null,
            }));
        } else {
            // interpret as "clear my filter" — many APIs treat empty array as reset
            payload = [];
        }

        setSaving(true);
        setError(null);
        try {
            await createGlobalCustomerFilter(user.username, payload);
            onClose?.(); // close on success
        } catch (e) {
            console.error('[GlobalCustomerForm] save error', e);
            // still no validation — just show a non-blocking message
            setError("Save failed.");
        } finally {
            setSaving(false);
        }
    }, [groupId, customerSelections, userId, user?.username, existingUcfIdByCustomer, onClose]);


    const onGroupSelect = (resOrVal) => {
        const id = resOrVal?.key ?? resOrVal?.id ?? resOrVal?.value ?? resOrVal;
        setCustomerGroup(Number(id) || null);  // store scalar
    };

    return (
        <div style={{ boxSizing: "border-box" }}>
            <h4 style={{ margin: 0 }}>Global User Customer Filter</h4>
            <div className="ui divider" />

            {loading && <div className="ui info message" style={{ marginBottom: 10 }}>Loading saved selection…</div>}
            {error && <div className="ui negative message" style={{ marginBottom: 10 }}>{error}</div>}

            <div className="ui form" style={{ margin: 0 }}>
                <div className="field" style={{ minWidth: 280 }}>
                    <SearchDropdown
                        key={`group-${groupId ?? 'none'}`}         // 🔑 force remount when id changes from null→number
                        editMode={true}
                        field="ucg_name"
                        label="User Customer Group"
                        placeholder="User Customer Group"
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        clearable
                        onClear={() => setCustomerGroup(null)}
                        onResultSelect={onGroupSelect}             // ✅ store scalar id
                        api={getOtherRefSearchDropdown}
                        apiDependency={{ type: "customer_group" }}
                        fieldInfo={{
                            tableName: "authentication.tms_a_user_customer_group",
                            primaryKey: "ucg_id",
                            fieldName: "ucg_name",
                            id: groupId ? groupId.toString() : "",   // ✅ ALWAYS use scalar id here
                        }}
                    />
                </div>

                <div className="field" style={{ width: "100%" }}>
                    <SearchDropdown
                        ref={searchRef}
                        form={FORM}
                        field="customer_search"
                        label="Customer"
                        editMode={true}
                        placeholder="Search Customer..."
                        multiple
                        multiValueTwoWayBinding
                        multiValues={customerSelections}
                        returnFullObject
                        hideMultiValueList={true}
                        threshold={1}
                        reset={resetCustomers}
                        setReset={setResetCustomers}
                        onResultSelect={handleSelectCustomer}
                        api={getOtherRefSearchDropdown}
                        apiDependency={customerDep}
                        disabled={!groupId || loading || saving}
                    />
                </div>
            </div>

            <div className="ui segment" style={{ marginTop: 10, minHeight: 70, display: "flex", alignItems: "flex-start", gap: 6, flexWrap: "wrap" }}>
                {Object.keys(customerSelections).length === 0 ? (
                    <span style={{ color: "#999" }}>No customers selected</span>
                ) : (
                    Object.entries(customerSelections).map(([id, label]) => (
                        <div key={id} className="ui label">
                            {label}
                            <i className="delete icon" onClick={() => removeSelection(Number(id))} style={{ cursor: "pointer" }} />
                        </div>
                    ))
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button className="ui button" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button primary className={`ui button ${saving ? "loading" : ""}`} onClick={handleSave} disabled={saving}>Save</Button>
            </div>
        </div>
    );
}
